'use client';

import { memo } from 'react';
import { css, cx } from '@bts-web/utils-style-engine';

export type DualProgressBarProps = {
  unstaking?: number;
  staking: number;
};

const clamp = (n: number) => Math.max(0, Math.min(100, n));

function getSegments(unstaking = 0, staking: number) {
  const a = clamp(unstaking);

  const b = clamp(staking);

  const sum = a + b;

  if (sum > 100) {
    const scale = 100 / sum;

    const aN = a * scale;

    const bN = b * scale;

    return { aN, bN, rest: Math.max(0, 100 - (aN + bN)) };
  }

  return { aN: a, bN: b, rest: Math.max(0, 100 - (a + b)) };
}

const barContainer = css({
  height: 6,
  borderRadius: 8,
  overflow: 'hidden',
  marginTop: 'small',
  marginBottom: 'small',
});

const inner = css({
  display: 'flex',
  gap: 2,
  backgroundColor: 'white',
  height: '100%',
  width: '100%',
});

const segBase = css({ height: '100%', flexShrink: 0 });

const segA = css({ backgroundColor: 'warning.fill_primary' });

const segB = css({ backgroundColor: 'neutrals.text_primary' });

const segRest = css({ backgroundColor: 'brand.fill_disabled', flexGrow: 1 });

function StakingProgressBarImpl({
  unstaking = 0,
  staking,
}: DualProgressBarProps) {
  const { aN, bN, rest } = getSegments(unstaking, staking);

  return (
    <div
      className={barContainer}
      role="img"
      aria-label="Progress bar for staked or unstaked"
      data-testid="dual-progress"
    >
      <div className={inner}>
        {unstaking !== 0 && (
          <div
            className={cx(segBase, segA)}
            style={{ width: `${aN}%` }}
            data-testid="dual-progress-a"
          />
        )}
        <div
          className={cx(segBase, segB)}
          style={{ width: `${bN}%` }}
          data-testid="dual-progress-b"
        />
        <div
          className={cx(segBase, segRest)}
          style={{ width: `${rest}%` }}
          data-testid="dual-progress-rest"
        />
      </div>
    </div>
  );
}

export const StakingProgressBar = memo(
  StakingProgressBarImpl,
  (prev, next) =>
    prev.staking === next.staking &&
    (prev.unstaking ?? 0) === (next.unstaking ?? 0),
);
